import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { errMsg } from "../lib/api";
import { toast } from "../lib/notices";

/*
 * "Continue with Google", via Google Identity Services (GIS) in popup mode.
 *
 * Google hands us a signed ID token (`credential`). We POST it to
 * /auth/google, where the backend verifies it against Google's keys and
 * issues our own session. No third party sits in between, and there is no
 * client secret.
 *
 * The GIS script is injected at runtime, not installed from npm. It loads at
 * most once per page, however many buttons mount.
 *
 * Renders nothing, not even the "or" divider, when /api/config has no
 * google_client_id.
 */
const GSI_SRC = "https://accounts.google.com/gsi/client";

let gsiLoading = null;

function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiLoading) return gsiLoading;
  gsiLoading = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => {
      // Allow a later mount to retry, e.g. after a network blip.
      gsiLoading = null;
      script.remove();
      reject(new Error("Google sign-in script failed to load"));
    }, { once: true });
  });
  return gsiLoading;
}

// google.accounts.id.initialize is page-global, and GIS warns if it is called
// again. Initialise once per client id, and send the callback to whichever
// button is mounted now (Login and Register each mount their own).
let initializedFor = "";
let activeHandler = null;

function ensureInitialized(clientId) {
  if (initializedFor === clientId) return;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => activeHandler?.(response),
    ux_mode: "popup",
  });
  initializedFor = clientId;
}

// GIS accepts a button width of 200 to 400 px.
const clampWidth = (px) => Math.max(200, Math.min(400, Math.round(px || 320)));

export default function GoogleSignInButton({ onSignedIn, testId = "google-signin" }) {
  const { googleClientId } = useConfig();
  const { loginWithGoogle } = useAuth();
  const slot = useRef(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // The GIS callback is registered once, but the page's success handler can
  // change between renders. Keep the latest one in a ref.
  const handleCredential = useRef(null);
  useEffect(() => {
    handleCredential.current = async (response) => {
      if (!response?.credential) {
        toast.error("Google sign-in failed");
        return;
      }
      setBusy(true);
      try {
        await loginWithGoogle(response.credential);
        onSignedIn?.();
      } catch (err) {
        toast.error(errMsg(err, "Google sign-in failed"));
      } finally {
        setBusy(false);
      }
    };
  });

  useEffect(() => {
    if (!googleClientId) return undefined;
    let alive = true;
    const mine = (response) => handleCredential.current?.(response);
    loadGsi()
      .then(() => {
        const el = slot.current;
        if (!alive || !el) return;
        ensureInitialized(googleClientId);
        activeHandler = mine;
        el.replaceChildren();  // StrictMode mounts twice; never stack two buttons
        window.google.accounts.id.renderButton(el, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: clampWidth(el.offsetWidth),
        });
        setFailed(false);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (activeHandler === mine) activeHandler = null;
    };
  }, [googleClientId]);

  if (!googleClientId) return null;

  return (
    <>
      <div className="my-6 text-center text-xs uppercase tracking-widest text-ink-subtle">— OR —</div>
      <div
        ref={slot}
        data-testid={testId}
        aria-busy={busy}
        className={`flex justify-center min-h-10 ${busy ? "opacity-60 pointer-events-none" : ""}`}
      />
      {failed && (
        <p role="status" className="mt-2 text-center text-xs text-ink-muted">
          Google sign-in could not load. Sign in with your email instead, or try again later.
        </p>
      )}
    </>
  );
}
